// react
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

import { withExtension } from '@karehero/helpers/files';

// hooks
import { useTracking, TrackingEvent } from 'hooks/useTracking';

// store
import { newToast } from 'store/slices/uiSlice';
import {
  useRequestOrganiseDocumentUploadURLMutation,
  useConfirmOrganiseDocumentUploadMutation,
  useDeleteOrganiseDocumentMutation,
  useKeepLiveRecordingMutation,
} from 'store/api/organiseDocument';

/**
 * useDocumentUpload owns the storage lifecycle of an organise document.
 *
 * - `uploadDocument` requests a signed URL, PUTs/POSTs the bytes, then confirms,
 *   resolving with the new document id. It throws on failure so the caller can
 *   reconcile its own state.
 * - `uploadRecording` keeps a voice note the API already holds from its live
 *   transcription session, uploading the file instead when it cannot be kept.
 * - `removeUpload` deletes a document from storage, reporting failures via
 *   Sentry + a toast; it resolves once the delete settles.
 *
 * Both emit PostHog events. State about which documents are pending is the
 * caller's concern (see useActionUploads).
 */
export interface UploadDocumentOptions {
  tags?: string[];
  /** What the upload answers, sent as its description: what the server hands on as its context. */
  context?: Record<string, string>;
}

/** A recording the API holds from its live session, and how long the app's own runs. */
export interface LiveRecording {
  id: string;
  /** The API refuses a recording much shorter than this, as having lost audio. */
  durationSeconds: number;
}

/** The API keeps a live recording as a WAV file. */
const LIVE_RECORDING_EXTENSION = 'wav';

export const useDocumentUpload = (careCircleId: string) => {
  const dispatch = useDispatch();
  const { track } = useTracking();
  const [requestUploadURL] = useRequestOrganiseDocumentUploadURLMutation();
  const [confirmUpload] = useConfirmOrganiseDocumentUploadMutation();
  const [keepLiveRecording] = useKeepLiveRecordingMutation();
  const [deleteDocument] = useDeleteOrganiseDocumentMutation();

  const uploadDocument = useCallback(
    async (file: File, options?: UploadDocumentOptions): Promise<string> => {
      track(TrackingEvent.DocumentUploadStarted, {
        careCircleId,
        contentType: file.type,
        sizeBytes: file.size,
      });

      const { documentId, uploadUrl } = await requestUploadURL({
        careCircleId,
        body: {
          title: file.name,
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
          ...(options?.tags && { tags: options.tags }),
          ...(options?.context && {
            description: JSON.stringify(options.context),
          }),
        },
      }).unwrap();

      if (!uploadUrl) {
        throw new Error('No upload URL in response');
      }

      // POST for the JSON emulator endpoint, PUT for production signed URLs.
      const isJSONUploadEndpoint = uploadUrl.includes('/upload/storage/');
      const res = await fetch(uploadUrl, {
        method: isJSONUploadEndpoint ? 'POST' : 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!res.ok) {
        throw new Error(`Upload to storage failed: ${await res.text()}`);
      }

      await confirmUpload({ careCircleId, body: { documentId } }).unwrap();

      track(TrackingEvent.DocumentUploaded, {
        careCircleId,
        contentType: file.type,
        sizeBytes: file.size,
      });

      return documentId;
    },
    [careCircleId, requestUploadURL, confirmUpload, track],
  );

  const uploadRecording = useCallback(
    async (
      file: File,
      recording: LiveRecording,
      options?: UploadDocumentOptions,
    ): Promise<string> => {
      try {
        const { documentId } = await keepLiveRecording({
          careCircleId,
          body: {
            recordingId: recording.id,
            durationSeconds: recording.durationSeconds,
            // Named for the file it is kept as, not the one recorded.
            title: withExtension(file.name, LIVE_RECORDING_EXTENSION),
            ...(options?.tags && { tags: options.tags }),
            ...(options?.context && {
              description: JSON.stringify(options.context),
            }),
          },
        }).unwrap();
        track(TrackingEvent.DocumentUploaded, {
          careCircleId,
          contentType: file.type,
          sizeBytes: file.size,
          isLiveRecording: true,
        });
        return documentId;
      } catch {
        // Expected when the API holds less than the whole recording (a
        // conflict, left out of error reports); any other failure is reported
        // by the API layer. Either way the file is uploaded instead.
        return uploadDocument(file, options);
      }
    },
    [careCircleId, keepLiveRecording, uploadDocument, track],
  );

  const removeUpload = useCallback(
    async (documentId: string): Promise<void> => {
      try {
        await deleteDocument({ careCircleId, documentId }).unwrap();
        track(TrackingEvent.DocumentRemoved, { careCircleId });
      } catch (error) {
        Sentry.captureException(error);
        dispatch(
          newToast({
            title: 'Couldn’t remove document',
            description: 'Please try again.',
          }),
        );
      }
    },
    [careCircleId, deleteDocument, dispatch, track],
  );

  return { uploadDocument, uploadRecording, removeUpload };
};
